/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/user_organization_roles-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-user_organization_roles-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/user_organization_roles-entity-config.ts
 * - Run: npm run hooks:gen (regenerates this file)
 * - Or run: npm run db:full-workflow (does everything)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: user_id, organization_id, role_id
 * - Sortable fields: created_at
 * - Required fields: user_id, organization_id, role_id
 * - Readonly fields: user_id, organization_id, role_id, created_at
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/user_organization_roles-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-user_organization_roles-api-client.ts (API client)
 * - client/api/generated_schemas/user_organization_roles.ts (TypeBox schemas and types)
 *
 * Generated at: 2026-06-22T23:44:13.692Z
 * Source config: config/entities/user_organization_roles-entity-config.ts
 * Public fields: 11/11 total fields
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  UserOrganizationRolesClient,
  UserOrganizationRolesQueryOptions,
} from "../generated_clients/generated-user_organization_roles-api-client.js";
import {
  UserOrganizationRolesInsert,
  UserOrganizationRolesUpdate,
  UserOrganizationRolesFieldConfig,
} from "../../types/generated_schemas/generated-user_organization_roles-schema.js";

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/user_organization_roles_business_hooks/

// Query keys with enhanced caching strategy
export const userOrganizationRolesKeys = {
  all: ["user_organization_roles"] as const,
  lists: () => [...userOrganizationRolesKeys.all, "list"] as const,
  list: (filters: Record<string, any>) =>
    [...userOrganizationRolesKeys.lists(), { filters }] as const,
  details: () => [...userOrganizationRolesKeys.all, "detail"] as const,
  detail: (id: string) => [...userOrganizationRolesKeys.details(), id] as const,
  search: (query: string, fields?: string[]) =>
    [...userOrganizationRolesKeys.all, "search", query, fields] as const,
  filter: (filters: Record<string, any>) =>
    [...userOrganizationRolesKeys.all, "filter", filters] as const,
  sort: (sortBy: string, ascending: boolean) =>
    [...userOrganizationRolesKeys.all, "sort", sortBy, ascending] as const,
  count: (filters?: Record<string, any>) =>
    [...userOrganizationRolesKeys.all, "count", filters] as const,
};

// Client instance hook - uses the shared httpClient
export const useUserOrganizationRolesClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new UserOrganizationRolesClient();
};

/**
 * Hook to get all user_organization_roles with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with user_organization_roles data
 */
export const useUserOrganizationRolesList = (
  options?: UserOrganizationRolesQueryOptions,
) => {
  const client = useUserOrganizationRolesClient();

  return useQuery({
    queryKey: userOrganizationRolesKeys.list(options?.filters || {}),
    queryFn: () => client.getAll(options),
  });
};

/**
 * Hook to get a single user_organization_role by ID
 *
 * @param id - The user_organization_role ID
 * @returns React Query result with single user_organization_role data
 */
export const useUserOrganizationRoles = (id: string) => {
  const client = useUserOrganizationRolesClient();

  return useQuery({
    queryKey: userOrganizationRolesKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/user_organization_roles
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/user_organization_roles_business_hooks/
 */

/**
 * Hook to create a new user_organization_role
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: user_id, organization_id, role_id
 * ⚠️ READONLY FIELDS: These fields cannot be set: user_id, organization_id, role_id, created_at
 *
 * @returns React Query mutation for creating user_organization_role
 */
export const useCreateUserOrganizationRoles = () => {
  const client = useUserOrganizationRolesClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UserOrganizationRolesInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: userOrganizationRolesKeys.all,
      });
    },
  });
};

/**
 * Hook to update an existing user_organization_role
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: user_id, organization_id, role_id, created_at
 *
 * @returns React Query mutation for updating user_organization_role
 */
export const useUpdateUserOrganizationRoles = () => {
  const client = useUserOrganizationRolesClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UserOrganizationRolesUpdate;
    }) => client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: userOrganizationRolesKeys.detail(id),
      });
      queryClient.invalidateQueries({
        queryKey: userOrganizationRolesKeys.lists(),
      });
    },
  });
};

/**
 * Hook to delete a user_organization_role
 *
 * @returns React Query mutation for deleting user_organization_role
 */
export const useDeleteUserOrganizationRoles = () => {
  const client = useUserOrganizationRolesClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: userOrganizationRolesKeys.all,
      });
    },
  });
};

/**
 * Hook to count user_organization_roles with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: user_id, organization_id, role_id
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const useUserOrganizationRolesCount = (
  filter?: Partial<
    Record<(typeof UserOrganizationRolesFieldConfig.filterable)[number], any>
  >,
) => {
  const client = useUserOrganizationRolesClient();

  return useQuery({
    queryKey: userOrganizationRolesKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const useUserOrganizationRolesFieldConfig = () => {
  return UserOrganizationRolesFieldConfig;
};
